import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import ManorShell from './ManorShell';

// The intro clip lives on S3 next to the other game assets: <REACT_APP_S3_PATH>main.mp4 (REACT_APP_INTRO_VIDEO_URL
// overrides it). The copy bundled with the app is only a fallback.
const LOCAL_VIDEO = '/manor/intro/main.mp4';
const S3_BASE = process.env.REACT_APP_S3_PATH;
const INTRO_VIDEO =
  process.env.REACT_APP_INTRO_VIDEO_URL || (S3_BASE ? `${S3_BASE.replace(/\/?$/, '/')}main.mp4` : LOCAL_VIDEO);

export default function ManorIntro() {
  const navigate = useNavigate();
  const videoRef = useRef(null);
  const game = useSelector((s) => s.manor.game);
  const session = useSelector((s) => s.manor.session);
  // The clip only shows once it is really playing; until then (or if it can't load / autoplay is
  // blocked) the page shows the title-screen art with the Begin button rather than a black screen.
  const [playing, setPlaying] = useState(false);
  const [videoSrc, setVideoSrc] = useState(INTRO_VIDEO);

  // Browsers block autoplay-with-sound until the page has been interacted with. Try with sound first
  // (the launcher click usually allows it), then fall back to muted playback rather than not playing.
  const tryPlay = () => {
    const v = videoRef.current;
    if (!v || !v.paused) return;
    v.play().catch(() => {
      v.muted = true;
      v.play().catch((e) => console.warn('Intro video could not start:', e.message));
    });
  };

  // Finished players can't replay (index.php: "You have played this game already").
  useEffect(() => {
    if (game?.gameover) {
      // OK sends them to the game's page on ExtraMilePlay (same target as the Back button).
      Swal.fire({ text: 'You have played this game already', icon: 'error', allowOutsideClick: false }).then(() => {
        window.location.href = session?.backButtonRedirect || process.env.REACT_APP_BASE_URL;
      });
    }
  }, [game, session]);

  const next = () => navigate('/manor/main');

  return (
    <ManorShell bg="main/bg.png" bgMob="img/main/bg_mob.jpg" className="manor-intro">
      <video
        ref={videoRef}
        className="manor-intro-video"
        style={{ visibility: playing ? 'visible' : 'hidden' }}
        src={videoSrc}
        autoPlay
        playsInline
        preload="metadata"
        onLoadedData={tryPlay}
        onCanPlay={tryPlay}
        onPlaying={() => setPlaying(true)}
        onError={() => {
          setPlaying(false);
          if (videoSrc !== LOCAL_VIDEO) setVideoSrc(LOCAL_VIDEO); // S3 copy unreachable: try the bundled one
        }}
        onEnded={next}
      />
      <button type="button" className="manor-img-btn manor-intro-begin" onClick={next}>
        <img src="/manor/img/BeginPlay.png" alt="Begin" />
      </button>
    </ManorShell>
  );
}
